import React, { useEffect } from 'react';
import { colors } from '../styles/colors';

const TermsOfServicePage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div 
      className="min-h-screen pt-24 pb-16 px-4 sm:px-6"
      style={{
        background: `linear-gradient(135deg, ${colors.black} 0%, ${colors.graphite} 100%)`,
      }}
    >
      <div className="max-w-4xl mx-auto">
        {/* Page Heading */}
        <h1 
          className="text-3xl sm:text-4xl md:text-5xl font-bold mb-2 text-center"
          style={{ color: colors.white }}
        >
          Terms of Service
        </h1>
        
        {/* Publication Date */}
        <p 
          className="text-sm text-center mb-8"
          style={{ color: colors.white, opacity: 0.6 }}
        >
          Last updated: 27/11/2025
        </p>

        {/* Download Button */}
        <div className="flex justify-center mb-12 sm:mb-16">
          <a
            href="/terms-of-service.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg transition-all duration-300 hover:scale-105"
            style={{
              backgroundColor: colors.teal,
              color: colors.white,
            }}
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              width="20" 
              height="20" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
            Download PDF
          </a>
        </div>

        {/* Document Content */}
        <div 
          className="prose prose-invert max-w-none"
          style={{ 
            color: colors.white,
            fontSize: '18px',
            lineHeight: '1.7'
          }}
        >
          <p style={{ marginBottom: '32px' }}>
            These Terms of Service ("Terms") constitute a legally binding agreement between you ("User", "you", or "your") and Arizore LTD ("Arizore", "we", "us", or "our") governing your access to and use of the Arizore website, application systems, user portal, related platforms, and any services we make available (collectively, the "Services"). By accessing or using the Services, you acknowledge that you have read, understood, and agree to be bound by these Terms. If you do not agree, you must immediately discontinue use.
          </p>

          <h2 style={{ fontSize: '28px', fontWeight: 'bold', marginTop: '32px', marginBottom: '16px', color: colors.white }}>
            1. Who We Are
          </h2>
          <p style={{ marginBottom: '24px' }}>
            Arizore LTD is a corporation established under the laws of Nevis. Arizore operates a structured real-world-asset ("RWA") reserve framework designed to support mining projects, bullion providers, and commercial entities seeking to participate in the AZ Reserve. Our Services include due-diligence workflows, application intake, expert review pathways, verified resource assessment processes, and secure user portal functionality.
          </p>
          <p style={{ marginBottom: '12px' }}>
            Registered Address: The Provident House, Central Government Road, Charlestown, Nevis, W.I.
          </p>
          <p style={{ marginBottom: '24px' }}>
            Legal Contact: admin@azreserves.com
          </p>
          <p style={{ marginBottom: '24px' }}>
            Arizore reserves the right to modify, suspend, or discontinue any aspect of the Services at any time.
          </p>

          <h2 style={{ fontSize: '28px', fontWeight: 'bold', marginTop: '32px', marginBottom: '16px', color: colors.white }}>
            2. Acceptance of Terms
          </h2>
          <p style={{ marginBottom: '12px' }}>
            By accessing or using the Services, you affirm that you:
          </p>
          <ul style={{ marginBottom: '24px', paddingLeft: '24px' }}>
            <li style={{ marginBottom: '8px' }}>Have read, understood, and agree to these Terms;</li>
            <li style={{ marginBottom: '8px' }}>Are capable of entering into legally binding contracts;</li>
            <li style={{ marginBottom: '8px' }}>Will comply with all applicable laws and regulations.</li>
          </ul>
          <p style={{ marginBottom: '24px' }}>
            If you are accessing the Services on behalf of a company or other legal entity, you represent that you have authority to bind such entity. If you do not have such authority, you may not use the Services.
          </p>

          <h2 style={{ fontSize: '28px', fontWeight: 'bold', marginTop: '32px', marginBottom: '16px', color: colors.white }}>
            3. Eligibility
          </h2>
          <p style={{ marginBottom: '12px' }}>
            You may use the Services only if:
          </p>
          <ul style={{ marginBottom: '24px', paddingLeft: '24px' }}>
            <li style={{ marginBottom: '8px' }}>You are at least 18 years of age;</li>
            <li style={{ marginBottom: '8px' }}>You are legally permitted to engage in the activities the Services support;</li>
            <li style={{ marginBottom: '8px' }}>You are not located in a jurisdiction where access to these Services is restricted.</li>
          </ul>
          <p style={{ marginBottom: '24px' }}>
            Access to the secure user portal is provided exclusively to individuals and entities that have completed a meet-and-greet call or preliminary verification phase with the Arizore team.
          </p>

          <h2 style={{ fontSize: '28px', fontWeight: 'bold', marginTop: '32px', marginBottom: '16px', color: colors.white }}>
            4. Nature of Arizore's Services
          </h2>
          <p style={{ marginBottom: '24px' }}>
            Arizore's ecosystem also includes marketing-related communications. By engaging with Arizore or submitting information through our forms, you acknowledge that marketing forms part of the broader AZ Reserve ecosystem, and Arizore may provide you with relevant informational or educational content relating to AZ Reserve, provided such communications comply with applicable laws and your communication preferences.
          </p>
          <p style={{ marginBottom: '12px' }}>
            Arizore provides a framework that supports:
          </p>
          <ul style={{ marginBottom: '24px', paddingLeft: '24px' }}>
            <li style={{ marginBottom: '8px' }}>A structured onboarding process for mining projects and bullion providers;</li>
            <li style={{ marginBottom: '8px' }}>Submission and assessment of documentation, reports, and verification materials;</li>
            <li style={{ marginBottom: '8px' }}>A secure user portal through which Users can monitor application progress;</li>
            <li style={{ marginBottom: '8px' }}>Visibility into reserve pathways, including pledged, verified, and delivered resources.</li>
          </ul>
          <p style={{ marginBottom: '12px' }}>
            Arizore does not:
          </p>
          <ul style={{ marginBottom: '24px', paddingLeft: '24px' }}>
            <li style={{ marginBottom: '8px' }}>Provide or purport to provide financial, investment, tax, or legal advice;</li>
            <li style={{ marginBottom: '8px' }}>Provide guarantees that any User will be approved to participate;</li>
            <li style={{ marginBottom: '8px' }}>Assure any future appreciation, valuation, liquidity, or performance of tokens or related assets.</li>
          </ul>
          <p style={{ marginBottom: '24px' }}>
            All decisions relating to onboarding and acceptance are made at Arizore's sole discretion.
          </p>

          <h2 style={{ fontSize: '28px', fontWeight: 'bold', marginTop: '32px', marginBottom: '16px', color: colors.white }}>
            5. Forward Contract Disclosures
          </h2>
          <p style={{ marginBottom: '12px' }}>
            Forward contract mechanisms facilitated through the AZ Reserve may involve, depending on project type:
          </p>
          <ul style={{ marginBottom: '24px', paddingLeft: '24px' }}>
            <li style={{ marginBottom: '8px' }}>Submission of geological surveys, business documents, certifications, or audit reports;</li>
            <li style={{ marginBottom: '8px' }}>Pledging of in-ground resources, tailings, or vaulted bullion;</li>
            <li style={{ marginBottom: '8px' }}>Delivery of refined bullion upon satisfaction of contract terms;</li>
            <li style={{ marginBottom: '8px' }}>Verification and compliance checks conducted by qualified third-party specialists.</li>
          </ul>
          <p style={{ marginBottom: '24px' }}>
            Forward contract arrangements vary significantly by project. Nothing contained within the Services shall be construed as a commitment by Arizore to enter into, offer, or fulfil a forward contract with any User.
          </p>

          <h2 style={{ fontSize: '28px', fontWeight: 'bold', marginTop: '32px', marginBottom: '16px', color: colors.white }}>
            6. Mining and Bullion Provider Participation
          </h2>

          <h3 style={{ fontSize: '22px', fontWeight: '600', marginTop: '24px', marginBottom: '12px', color: colors.white }}>
            6.1 Mining Projects
          </h3>
          <p style={{ marginBottom: '24px' }}>
            Mining entities seeking to participate in the AZ Reserve must undergo multi-layered due-diligence, which may include technical review, compliance verification, geological assessment, and project validation. Submission of documentation does not guarantee acceptance.
          </p>

          <h3 style={{ fontSize: '22px', fontWeight: '600', marginTop: '24px', marginBottom: '12px', color: colors.white }}>
            6.2 Bullion Providers
          </h3>
          <p style={{ marginBottom: '24px' }}>
            Bullion providers may sell vaulted bullion into the AZ Reserve through verified transfer pathways. Upon transfer, bullion providers relinquish ownership of the bullion. Any tokens issued as part of a pathway reflect audited and verified reserve structures and are subject to separate agreements.
          </p>
          <p style={{ marginBottom: '24px' }}>
            Arizore retains full discretion to refuse, delay, or terminate onboarding for any reason, including risk concerns, incomplete documentation, or regulatory conflict.
          </p>

          <h2 style={{ fontSize: '28px', fontWeight: 'bold', marginTop: '32px', marginBottom: '16px', color: colors.white }}>
            7. Token Disclosures
          </h2>
          <p style={{ marginBottom: '24px' }}>
            Arizore's ecosystem includes asset-backed digital tokens associated with verified reserves of in-ground resources, tailings, or vaulted bullion. These tokens may be characterized as security tokens under various regulatory frameworks.
          </p>
          <p style={{ marginBottom: '12px' }}>
            Important Legal Disclaimers:
          </p>
          <ul style={{ marginBottom: '24px', paddingLeft: '24px' }}>
            <li style={{ marginBottom: '8px' }}>Tokens may experience material price volatility, including loss of value.</li>
            <li style={{ marginBottom: '8px' }}>Token value is influenced by reserve composition, macroeconomic factors, and market conditions.</li>
            <li style={{ marginBottom: '8px' }}>Arizore does not guarantee liquidity, market access, convertibility, or redemption pathways.</li>
            <li style={{ marginBottom: '8px' }}>The existence of a token does not imply rights beyond those expressly documented and contractually provided.</li>
            <li style={{ marginBottom: '8px' }}>Token pathways may be restricted or unavailable in certain jurisdictions.</li>
          </ul>
          <p style={{ marginBottom: '24px' }}>
            Users acknowledge and agree that participation in tokenized pathways involves inherent risk.
          </p>

          <h2 style={{ fontSize: '28px', fontWeight: 'bold', marginTop: '32px', marginBottom: '16px', color: colors.white }}>
            8. No Financial or Investment Advice
          </h2>
          <p style={{ marginBottom: '12px' }}>
            All information provided through the Services—including reserve data, documentation, projections, or informational diagrams—is provided strictly for informational purposes and should not be interpreted as:
          </p>
          <ul style={{ marginBottom: '24px', paddingLeft: '24px' }}>
            <li style={{ marginBottom: '8px' }}>Financial or investment advice;</li>
            <li style={{ marginBottom: '8px' }}>A solicitation to buy or sell any asset;</li>
            <li style={{ marginBottom: '8px' }}>A recommendation regarding any token or pathway.</li>
          </ul>
          <p style={{ marginBottom: '24px' }}>
            Users are solely responsible for obtaining independent financial, legal, tax, accounting, or technical advice.
          </p>

          <h2 style={{ fontSize: '28px', fontWeight: 'bold', marginTop: '32px', marginBottom: '16px', color: colors.white }}>
            9. Account Registration and Security
          </h2>
          <p style={{ marginBottom: '12px' }}>
            Users granted portal access agree to:
          </p>
          <ul style={{ marginBottom: '24px', paddingLeft: '24px' }}>
            <li style={{ marginBottom: '8px' }}>Provide truthful and complete information during onboarding;</li>
            <li style={{ marginBottom: '8px' }}>Maintain the confidentiality of login credentials;</li>
            <li style={{ marginBottom: '8px' }}>Not permit unauthorized individuals to access their account;</li>
            <li style={{ marginBottom: '8px' }}>Immediately notify Arizore of suspected unauthorized access or breach;</li>
            <li style={{ marginBottom: '8px' }}>Accept full responsibility for all activities occurring under their account.</li>
          </ul>
          <p style={{ marginBottom: '24px' }}>
            Arizore may suspend, restrict, or revoke access for violations of these Terms or when deemed necessary to protect operational integrity.
          </p>

          <h2 style={{ fontSize: '28px', fontWeight: 'bold', marginTop: '32px', marginBottom: '16px', color: colors.white }}>
            10. Prohibited Use
          </h2>
          <p style={{ marginBottom: '12px' }}>
            Users may not engage in any activity that:
          </p>
          <ul style={{ marginBottom: '24px', paddingLeft: '24px' }}>
            <li style={{ marginBottom: '8px' }}>Scrapes, manipulates, or harvests data from the Services;</li>
            <li style={{ marginBottom: '8px' }}>Attempts to bypass, disable, or interfere with security controls;</li>
            <li style={{ marginBottom: '8px' }}>Submits fraudulent, falsified, or misleading documents;</li>
            <li style={{ marginBottom: '8px' }}>Uses automated scripts, bots, scraping tools, or extraction mechanisms;</li>
            <li style={{ marginBottom: '8px' }}>Attempts unauthorized access to systems, accounts, databases, or data;</li>
            <li style={{ marginBottom: '8px' }}>Violates applicable laws, regulatory restrictions, or contract terms.</li>
          </ul>
          <p style={{ marginBottom: '24px' }}>
            Arizore reserves the right to take legal action, including seeking damages or reporting unlawful conduct to authorities.
          </p>

          <h2 style={{ fontSize: '28px', fontWeight: 'bold', marginTop: '32px', marginBottom: '16px', color: colors.white }}>
            11. Intellectual Property
          </h2>
          <p style={{ marginBottom: '24px' }}>
            All content and materials accessible through the Services—including text, branding, logos, graphics, design elements, diagrams, software, and documentation—are owned by Arizore or its licensors and are protected by intellectual property laws.
          </p>
          <p style={{ marginBottom: '12px' }}>
            Users may not:
          </p>
          <ul style={{ marginBottom: '24px', paddingLeft: '24px' }}>
            <li style={{ marginBottom: '8px' }}>Copy, reproduce, distribute, or publicly display protected materials;</li>
            <li style={{ marginBottom: '8px' }}>Create derivative works, reverse engineer, or attempt to extract source code;</li>
            <li style={{ marginBottom: '8px' }}>Use Arizore trademarks without express written consent.</li>
          </ul>
          <p style={{ marginBottom: '24px' }}>
            Nothing in these Terms grants Users any rights to Arizore intellectual property except as explicitly permitted.
          </p>

          <h2 style={{ fontSize: '28px', fontWeight: 'bold', marginTop: '32px', marginBottom: '16px', color: colors.white }}>
            12. Accuracy of Information
          </h2>
          <p style={{ marginBottom: '12px' }}>
            Arizore endeavours to maintain accurate and current information, including reserve snapshots and onboarding documentation. However:
          </p>
          <ul style={{ marginBottom: '24px', paddingLeft: '24px' }}>
            <li style={{ marginBottom: '8px' }}>Data may be updated manually and may contain delays or inaccuracies;</li>
            <li style={{ marginBottom: '8px' }}>Reserve metrics, compositions, or values may change without notice;</li>
            <li style={{ marginBottom: '8px' }}>Arizore provides all information "as is" with no guarantees regarding comprehensiveness or suitability.</li>
          </ul>
          <p style={{ marginBottom: '24px' }}>
            Users acknowledge that operational, geological, and market data are subject to revision and review.
          </p>

          <h2 style={{ fontSize: '28px', fontWeight: 'bold', marginTop: '32px', marginBottom: '16px', color: colors.white }}>
            13. Disclaimers
          </h2>
          <p style={{ marginBottom: '24px' }}>
            To the fullest extent permitted by law, Arizore disclaims all warranties, express or implied, including warranties of merchantability, fitness for a particular purpose, non-infringement, accuracy, reliability, availability, and performance.
          </p>
          <p style={{ marginBottom: '12px' }}>
            We do not warrant that:
          </p>
          <ul style={{ marginBottom: '24px', paddingLeft: '24px' }}>
            <li style={{ marginBottom: '8px' }}>The Services will operate uninterrupted or error-free;</li>
            <li style={{ marginBottom: '8px' }}>Data will be accurate, complete, or timely;</li>
            <li style={{ marginBottom: '8px' }}>Communications or uploads will always be secure.</li>
          </ul>

          <h2 style={{ fontSize: '28px', fontWeight: 'bold', marginTop: '32px', marginBottom: '16px', color: colors.white }}>
            14. Limitation of Liability
          </h2>
          <p style={{ marginBottom: '12px' }}>
            To the maximum extent allowed by law, Arizore shall not be liable for:
          </p>
          <ul style={{ marginBottom: '24px', paddingLeft: '24px' }}>
            <li style={{ marginBottom: '8px' }}>Indirect, incidental, punitive, or consequential damages;</li>
            <li style={{ marginBottom: '8px' }}>Loss of profits, revenue, business, goodwill, or data;</li>
            <li style={{ marginBottom: '8px' }}>Damages arising from security breaches, unauthorized access, or data loss;</li>
            <li style={{ marginBottom: '8px' }}>Errors, omissions, delays, or inaccuracies in information.</li>
          </ul>
          <p style={{ marginBottom: '24px' }}>
            In all cases, Arizore's total liability shall not exceed the amount paid (if any) by the User to access the Services during the twelve months preceding the claim.
          </p>

          <h2 style={{ fontSize: '28px', fontWeight: 'bold', marginTop: '32px', marginBottom: '16px', color: colors.white }}>
            15. Indemnification
          </h2>
          <p style={{ marginBottom: '12px' }}>
            Users agree to defend, indemnify, and hold harmless Arizore, its affiliates, commercial partners, officers, employees, and agents from and against all claims, liabilities, damages, losses, costs, and expenses arising from:
          </p>
          <ul style={{ marginBottom: '24px', paddingLeft: '24px' }}>
            <li style={{ marginBottom: '8px' }}>Violation of these Terms;</li>
            <li style={{ marginBottom: '8px' }}>Misuse of the Services;</li>
            <li style={{ marginBottom: '8px' }}>Submission of false or misleading information;</li>
            <li style={{ marginBottom: '8px' }}>Violation of any applicable law or third-party right.</li>
          </ul>

          <h2 style={{ fontSize: '28px', fontWeight: 'bold', marginTop: '32px', marginBottom: '16px', color: colors.white }}>
            16. Governing Law & Dispute Resolution
          </h2>
          <p style={{ marginBottom: '24px' }}>
            These Terms are governed by the laws of Nevis, without regard to conflict of-laws principles. Users agree that any dispute arising under or relating to these Terms shall be resolved exclusively by the courts of Nevis.
          </p>
          <p style={{ marginBottom: '24px' }}>
            Arizore may seek injunctive relief or equitable remedies in any jurisdiction as necessary to protect its rights or intellectual property.
          </p>

          <h2 style={{ fontSize: '28px', fontWeight: 'bold', marginTop: '32px', marginBottom: '16px', color: colors.white }}>
            17. Changes to These Terms
          </h2>
          <p style={{ marginBottom: '24px' }}>
            Arizore may revise these Terms at any time. Material updates will be posted on this page with an updated "Last Updated" date. Continued use of the Services constitutes acceptance of the revised Terms.
          </p>

          <h2 style={{ fontSize: '28px', fontWeight: 'bold', marginTop: '32px', marginBottom: '16px', color: colors.white }}>
            18. Contact Information
          </h2>
          <p style={{ marginBottom: '12px' }}>
            For questions or concerns regarding these Terms of Service, please contact:
          </p>
          <p style={{ marginBottom: '12px' }}>
            Email: admin@azreserves.com
          </p>
          <p style={{ marginBottom: '24px' }}>
            Address: The Provident House, Central Government Road, Charlestown, Nevis, W.I.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TermsOfServicePage;
